import { useState, useCallback, useMemo } from 'react';

import { BookmarkIcon, PlusIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

import { FilterOptionsPanel } from './FilterOptionsPanel';
import { FILTER_ACTIVE_CLASSES, FILTER_SUBMENU_WIDTH } from './filterStyles';
import {
  FilterDefinition,
  FilterItem,
  FilterInputType,
  FilterOption,
  SavedFilterItem,
} from './types';

export type { FilterDefinition, FilterInputType, FilterOption, SavedFilterItem };

export interface ComplexFilterComboboxProps {
  filterDefinitions: FilterDefinition[];
  filters: FilterItem[];
  setFilter: (key: string, values: string[], exclude?: boolean) => void;
  removeFilter: (...keys: string[]) => void;
  activeFilterCount: number;
  savedFilters?: SavedFilterItem[];
  onApplySavedFilter?: (filter: SavedFilterItem) => void;
  /** Callback when clicking a filter category that's already active - opens pill popover */
  onOpenActivePill?: (filterId: string) => void;
  /** Show the "Clear · All" bulk actions inside each checkbox filter panel. Defaults to true. */
  showBulkActions?: boolean;
}

type ViewState = 'categories' | FilterDefinition;

export const ComplexFilterCombobox = ({
  filterDefinitions,
  filters,
  setFilter,
  removeFilter,
  activeFilterCount,
  savedFilters,
  onApplySavedFilter,
  onOpenActivePill,
  showBulkActions = true,
}: ComplexFilterComboboxProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<ViewState>('categories');
  const [searchQuery, setSearchQuery] = useState('');
  const [isSelectOpen, setIsSelectOpen] = useState(false);

  const activeFilter = view !== 'categories' ? view : null;

  const handleOpenChange = useCallback(
    (newOpen: boolean) => {
      if (!newOpen && isSelectOpen) {
        return;
      }
      setOpen(newOpen);
      if (!newOpen) {
        // Delay to allow close animation
        setTimeout(() => {
          setView('categories');
          setSearchQuery('');
        }, 150);
      }
    },
    [isSelectOpen]
  );

  const handleFilterSelect = useCallback(
    (filterDef: FilterDefinition) => {
      const isActive = filters.some((f) => f.key === filterDef.id && f.values.length > 0);

      if (isActive && onOpenActivePill) {
        onOpenActivePill(filterDef.id);
        setOpen(false);
        return;
      }

      setView(filterDef);
      setSearchQuery('');
    },
    [filters, onOpenActivePill]
  );

  const handleBack = useCallback(() => {
    setView('categories');
    setSearchQuery('');
  }, []);

  const handleValueChange = useCallback(
    (value: string | string[] | undefined, exclude?: boolean) => {
      if (!activeFilter) {
        return;
      }
      if (value === undefined) {
        removeFilter(activeFilter.id);
      } else {
        const values = Array.isArray(value) ? value : [value];
        setFilter(activeFilter.id, values, exclude);
      }
    },
    [activeFilter, setFilter, removeFilter]
  );

  const searchableFilterOptions = useMemo(() => {
    if (!searchQuery.trim()) {
      return [];
    }

    const query = searchQuery.toLowerCase();
    const results: Array<{ filterDef: FilterDefinition; option: FilterOption }> = [];

    filterDefinitions.forEach((filterDef) => {
      filterDef.options.forEach((option) => {
        if (option.label.toLowerCase().includes(query)) {
          results.push({ filterDef, option });
        }
      });
    });

    return results;
  }, [filterDefinitions, searchQuery]);

  const handleOptionSelect = useCallback(
    (filterDef: FilterDefinition, option: FilterOption) => {
      const currentFilter = filters.find((f) => f.key === filterDef.id);
      if (filterDef.type === 'checkbox') {
        const currentValues = (currentFilter?.values as string[]) || [];
        const newValues = currentValues.includes(option.value)
          ? currentValues.filter((v) => v !== option.value)
          : [...currentValues, option.value];
        setFilter(filterDef.id, newValues, currentFilter?.exclude);
      } else {
        setFilter(filterDef.id, [option.value], currentFilter?.exclude);
      }

      setOpen(false);
    },
    [filters, setFilter]
  );

  const renderCategoriesView = () => {
    const isSearching = searchQuery.trim().length > 0;

    const matchingCategories = isSearching
      ? filterDefinitions.filter((f) => f.label.toLowerCase().includes(searchQuery.toLowerCase()))
      : filterDefinitions;

    const matchingSavedFilters =
      isSearching && savedFilters
        ? savedFilters.filter((f) => f.name.toLowerCase().includes(searchQuery.toLowerCase()))
        : savedFilters;

    // Searching view: flat list
    if (isSearching) {
      return (
        <>
          <CommandInput
            placeholder={t('common.search')}
            value={searchQuery}
            onValueChange={setSearchQuery}
          />
          <CommandList>
            <CommandEmpty>{t('common.no_results')}</CommandEmpty>
            <CommandGroup>
              {/* Saved filters */}
              {matchingSavedFilters?.map((savedFilter) => (
                <CommandItem
                  key={savedFilter.id}
                  value={savedFilter.id}
                  onSelect={() => {
                    onApplySavedFilter?.(savedFilter);
                    setOpen(false);
                  }}
                >
                  <BookmarkIcon className="text-muted-foreground" />
                  <span>{savedFilter.name}</span>
                </CommandItem>
              ))}

              {/* Filter categories */}
              {matchingCategories.map((filterDef) => {
                const Icon = filterDef.icon;
                return (
                  <CommandItem
                    key={filterDef.id}
                    value={filterDef.label}
                    onSelect={() => handleFilterSelect(filterDef)}
                  >
                    <Icon className="text-muted-foreground" />
                    <span>{filterDef.label}</span>
                  </CommandItem>
                );
              })}

              {/* Filter options */}
              {searchableFilterOptions.map(({ filterDef, option }) => {
                const Icon = option.icon;
                return (
                  <CommandItem
                    key={`${filterDef.id}-${option.value}`}
                    value={`${filterDef.label} ${option.label}`}
                    onSelect={() => handleOptionSelect(filterDef, option)}
                  >
                    <div className="flex flex-col">
                      <span className="text-xs text-muted-foreground">{filterDef.label}</span>
                      <span className="flex items-center gap-2">
                        {Icon &&
                          (typeof Icon === 'string' ? (
                            <span className="text-sm">{Icon}</span>
                          ) : (
                            <Icon className="h-3 w-3 text-muted-foreground" />
                          ))}
                        {option.label}
                      </span>
                    </div>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </>
      );
    }

    // Default view: grouped
    return (
      <>
        <CommandInput
          placeholder={t('common.search')}
          value={searchQuery}
          onValueChange={setSearchQuery}
        />
        <CommandList>
          <CommandEmpty>{t('common.no_results')}</CommandEmpty>

          {/* Saved filters */}
          {matchingSavedFilters && matchingSavedFilters.length > 0 && onApplySavedFilter && (
            <>
              <CommandGroup heading={t('brain_logs.saved_filter_views')}>
                {matchingSavedFilters.map((savedFilter) => (
                  <CommandItem
                    key={savedFilter.id}
                    value={savedFilter.id}
                    onSelect={() => {
                      onApplySavedFilter(savedFilter);
                      setOpen(false);
                    }}
                  >
                    <BookmarkIcon className="text-muted-foreground" />
                    <span>{savedFilter.name}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
              <CommandSeparator />
            </>
          )}

          {/* Filter categories */}
          <CommandGroup>
            {filterDefinitions.map((filterDef) => {
              const Icon = filterDef.icon;
              return (
                <CommandItem
                  key={filterDef.id}
                  value={filterDef.label}
                  onSelect={() => handleFilterSelect(filterDef)}
                >
                  <Icon className="text-muted-foreground" />
                  <span>{filterDef.label}</span>
                </CommandItem>
              );
            })}
          </CommandGroup>
        </CommandList>
      </>
    );
  };

  const renderFilterOptionsView = () => {
    if (!activeFilter) {
      return null;
    }

    const { id, label, overlayLabel, type, options, placeholder, condition } = activeFilter;
    const currentItem = filters.find((f) => f.key === id);

    const currentValues = currentItem?.values;
    const currentValue = type === 'radio' || type === 'text' ? currentValues?.[0] : currentValues;

    return (
      <FilterOptionsPanel
        title={overlayLabel || label}
        type={type}
        options={options}
        placeholder={placeholder}
        condition={condition}
        value={currentValue}
        onValueChange={handleValueChange}
        exclude={currentItem?.exclude}
        onBack={handleBack}
        onSelectOpenChange={setIsSelectOpen}
        showBulkActions={showBulkActions}
      />
    );
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="outline-neutral"
          size="sm"
          className={cn(activeFilterCount > 0 && FILTER_ACTIVE_CLASSES)}
        >
          <PlusIcon />
          {t('common.filters')} {activeFilterCount > 0 && `(${activeFilterCount})`}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className={cn('p-0', view === 'categories' ? 'w-56' : FILTER_SUBMENU_WIDTH)}
        align="end"
        onInteractOutside={(e) => {
          if (isSelectOpen) {
            e.preventDefault();
          }
        }}
      >
        <Command shouldFilter={false}>
          {view === 'categories' ? renderCategoriesView() : renderFilterOptionsView()}
        </Command>
      </PopoverContent>
    </Popover>
  );
};
